import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Sparkles, ChefHat, ArrowDownToLine, Bed, Bath, Shirt, BedDouble, Sofa, Map as MapIcon, Camera, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { MobileOptionsSheet } from './MobileOptionsSheet';
import { interiorMenuData, InteriorMenuItem } from '../../types/interiorConfig';
const rooms = [{
  id: 'kitchen',
  name: 'Kitchen',
  floor: 'Main Floor',
  icon: ChefHat,
  planPosition: {
    top: '35%',
    left: '30%'
  }
}, {
  id: 'basement',
  name: 'Basement',
  floor: 'Basement',
  icon: ArrowDownToLine,
  planPosition: {
    top: '50%',
    left: '50%'
  }
}, {
  id: 'primary-bedroom',
  name: 'Primary Bedroom',
  floor: 'Upper Floor',
  icon: Bed,
  planPosition: {
    top: '25%',
    left: '70%'
  }
}, {
  id: 'ensuite',
  name: 'Ensuite',
  floor: 'Upper Floor',
  icon: Bath,
  planPosition: {
    top: '40%',
    left: '75%'
  }
}, {
  id: 'laundry',
  name: 'Laundry',
  floor: 'Main Floor',
  icon: Shirt,
  planPosition: {
    top: '70%',
    left: '25%'
  }
}, {
  id: 'bedroom-2',
  name: 'Bedroom 2',
  floor: 'Upper Floor',
  icon: BedDouble,
  planPosition: {
    top: '60%',
    left: '65%'
  }
}, {
  id: 'bedroom-3',
  name: 'Bedroom 3',
  floor: 'Upper Floor',
  icon: BedDouble,
  planPosition: {
    top: '75%',
    left: '70%'
  }
}, {
  id: 'living-room',
  name: 'Living Room',
  floor: 'Main Floor',
  icon: Sofa,
  planPosition: {
    top: '55%',
    left: '45%'
  }
}];
export function QuickNavInteriorViewer() {
  const [activeRoomIndex, setActiveRoomIndex] = useState(0);
  const [activeItem, setActiveItem] = useState('Countertop');
  const [isAssistantActive, setIsAssistantActive] = useState(false);
  const [sheetState, setSheetState] = useState<'collapsed' | 'peek' | 'expanded'>('peek');
  const [viewMode, setViewMode] = useState<'map' | '3d'>('3d');
  const [activeCameraIndex, setActiveCameraIndex] = useState(1);
  const [showTeleportMenu, setShowTeleportMenu] = useState(false);
  const [slideDirection, setSlideDirection] = useState(1);
  const flatSteps = useMemo(() => {
    const steps: {
      item: InteriorMenuItem;
      category: string;
    }[] = [];
    const traverse = (items: InteriorMenuItem[], parentCategory: string) => {
      items.forEach(item => {
        if (item.type === 'item' && (item.options || !item.children)) {
          steps.push({
            item,
            category: parentCategory
          });
        }
        if (item.children) {
          traverse(item.children, item.type === 'category' ? item.name : parentCategory);
        }
      });
    };
    traverse(interiorMenuData, 'General');
    return steps;
  }, []);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = flatSteps[currentStepIndex];
  const handleNextStep = () => {
    if (currentStepIndex < flatSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      setActiveItem(flatSteps[currentStepIndex + 1].item.name);
    }
  };
  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setActiveItem(flatSteps[currentStepIndex - 1].item.name);
    }
  };
  const handleActiveItemChange = (itemName: string) => {
    setActiveItem(itemName);
    const stepIdx = flatSteps.findIndex(s => s.item.name === itemName);
    if (stepIdx >= 0) {
      setCurrentStepIndex(stepIdx);
    }
  };
  const handleNextRoom = () => {
    setSlideDirection(1);
    setActiveRoomIndex(prev => (prev + 1) % rooms.length);
  };
  const handlePrevRoom = () => {
    setSlideDirection(-1);
    setActiveRoomIndex(prev => (prev - 1 + rooms.length) % rooms.length);
  };
  const handleSelectRoom = (index: number) => {
    setSlideDirection(index > activeRoomIndex ? 1 : -1);
    setActiveRoomIndex(index);
    setShowTeleportMenu(false);
  };
  const currentRoom = rooms[activeRoomIndex];
  const prevRoom = rooms[(activeRoomIndex - 1 + rooms.length) % rooms.length];
  const nextRoom = rooms[(activeRoomIndex + 1) % rooms.length];
  const pressTimer = useRef<NodeJS.Timeout | null>(null);
  const handlePointerDown = (action: 'next' | 'prev') => {
    pressTimer.current = setTimeout(() => {
      setShowTeleportMenu(true);
      pressTimer.current = null;
    }, 500);
  };
  const handlePointerUp = (action: 'next' | 'prev') => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
      if (action === 'next') handleNextRoom();else handlePrevRoom();
    }
  };
  const handlePointerLeave = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  useEffect(() => {
    return () => {
      if (pressTimer.current) clearTimeout(pressTimer.current);
    };
  }, []);
  return <div className="relative w-full h-full bg-black font-sans overflow-hidden" data-id="element-2091">
      {/* Viewport */}
      <div className="absolute inset-0" data-id="element-2092">
        {viewMode === '3d' ? <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1920&q=80" alt="Interior View" className="w-full h-full object-cover" data-id="element-2093" /> : <div className="w-full h-full bg-slate-900 flex items-center justify-center p-4 pt-32 pb-64" data-id="element-2094">
            <div className="relative w-full max-w-md aspect-[3/4] rounded-2xl overflow-hidden bg-slate-800 shadow-2xl" data-id="element-2095">
              <img src="https://cdn.magicpatterns.com/uploads/uRMUKbZyW7b6N3baMWficb/Zight_2026-04-17_at_6.48.42_AM.jpg" alt="Dollhouse Plan" className="w-full h-full object-contain" data-id="element-2096" />
              {rooms.map((room, idx) => {
            const Icon = room.icon;
            const isActive = activeRoomIndex === idx;
            return <button key={room.id} onClick={() => handleSelectRoom(idx)} className="absolute group" style={{
              top: room.planPosition.top,
              left: room.planPosition.left,
              transform: 'translate(-50%, -50%)'
            }} data-id="element-2097">
                    <span className="relative flex h-8 w-8" data-id="element-2098">
                      {isActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" data-id="element-2099" />}
                      <span className={`relative inline-flex rounded-full h-8 w-8 border-2 items-center justify-center shadow-lg transition-colors ${isActive ? 'bg-blue-500 border-white' : 'bg-white/90 border-white/50 group-active:bg-blue-400'}`} data-id="element-2100">
                        <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-slate-700'}`} data-id="element-2101" />
                      </span>
                    </span>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap" data-id="element-2102">
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-blue-500 text-white' : 'bg-black/60 text-white/80'}`} data-id="element-2103">
                        {room.name}
                      </span>
                    </div>
                  </button>;
          })}
            </div>
          </div>}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/30 pointer-events-none" data-id="element-2104" />
      </div>

      {/* Top Controls */}
      <div className="absolute top-0 left-0 right-0 z-40 pt-2 md:pt-14 px-4 flex flex-col gap-4" data-id="element-2105">
        {/* Row 1: Pills */}
        <div className="flex items-center justify-between" data-id="element-2106">
          {/* Map / 3D Toggle */}
          <div className="flex bg-black/40 backdrop-blur-xl rounded-full p-1 border border-white/10 shadow-lg" data-id="element-2107">
            <button onClick={() => setViewMode('map')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${viewMode === 'map' ? 'bg-white/20 text-white shadow-sm' : 'text-white/50 hover:text-white/80'}`} data-id="element-2108">
              <MapIcon className="w-3.5 h-3.5" data-id="element-2109" />
              Layout
            </button>
            <button onClick={() => setViewMode('3d')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${viewMode === '3d' ? 'bg-white/20 text-white shadow-sm' : 'text-white/50 hover:text-white/80'}`} data-id="element-2110">
              <div className="w-3.5 h-3.5" data-id="element-2111" />
              3D View
            </button>
          </div>

          {/* Camera Selector */}
          <div className="flex items-center bg-black/40 backdrop-blur-xl rounded-full p-1 border border-white/10 shadow-lg" data-id="element-2112">
            <Camera className="w-3.5 h-3.5 text-white/40 ml-2.5 mr-1.5" data-id="element-2113" />
            {[1, 2, 3, 4].map(cam => <button key={cam} onClick={() => setActiveCameraIndex(cam)} className={`w-7 h-7 rounded-full text-xs font-bold transition-all flex items-center justify-center ${activeCameraIndex === cam ? 'bg-white/20 text-white shadow-sm' : 'text-white/50 hover:text-white/80'}`} data-id="element-2114">
                {cam}
              </button>)}
          </div>
        </div>

        {/* Row 2: Room Navigation */}
        <div className="relative flex items-center justify-between h-12" data-id="element-2115">
          <button onPointerDown={() => handlePointerDown('prev')} onPointerUp={() => handlePointerUp('prev')} onPointerLeave={handlePointerLeave} className="relative z-10 w-10 h-10 rounded-full bg-black/40 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white/80 active:scale-95 transition-transform shrink-0 shadow-lg" data-id="element-2116">
            <ChevronLeft className="w-5 h-5" data-id="element-2117" />
          </button>

          <div className="flex-1 flex items-center overflow-hidden relative h-full px-1" data-id="element-2118">
            <div className="text-white/30 text-[10px] font-medium truncate w-12 text-left shrink-0" data-id="element-2119">
              {prevRoom.name}
            </div>
            <div className="flex-1 relative h-full flex items-center justify-center overflow-hidden mx-1" data-id="element-2120">
              <AnimatePresence mode="popLayout" custom={slideDirection} data-id="element-2121">
                <motion.div key={currentRoom.id} custom={slideDirection} initial={{
                opacity: 0,
                x: slideDirection * 40
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: slideDirection * -40
              }} transition={{
                type: 'spring',
                damping: 25,
                stiffness: 300
              }} className={`text-white font-bold tracking-wide text-center whitespace-nowrap absolute inset-0 flex items-center justify-center ${currentRoom.name.length > 12 ? 'text-sm' : 'text-lg'}`} data-id="element-2122">
                  {currentRoom.name}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="text-white/30 text-[10px] font-medium truncate w-12 text-right shrink-0" data-id="element-2123">
              {nextRoom.name}
            </div>
          </div>

          <button onPointerDown={() => handlePointerDown('next')} onPointerUp={() => handlePointerUp('next')} onPointerLeave={handlePointerLeave} className="relative z-10 w-10 h-10 rounded-full bg-black/40 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white/80 active:scale-95 transition-transform shrink-0 shadow-lg" data-id="element-2124">
            <ChevronRight className="w-5 h-5" data-id="element-2125" />
          </button>
        </div>
      </div>

      {/* Teleport Menu Portal */}
      {createPortal(<AnimatePresence data-id="element-2126">
          {showTeleportMenu && <>
              <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm" onClick={() => setShowTeleportMenu(false)} data-id="element-2127" />
              <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="fixed z-[201] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-sm bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden" data-id="element-2128">
                <div className="flex items-center justify-between p-4 border-b border-white/10" data-id="element-2129">
                  <h3 className="text-white font-semibold" data-id="element-2130">Teleport to Room</h3>
                  <button onClick={() => setShowTeleportMenu(false)} className="p-1 rounded-full bg-white/10 text-white/60 hover:text-white" data-id="element-2131">
                    <X className="w-4 h-4" data-id="element-2132" />
                  </button>
                </div>
                <div className="p-2 max-h-[50vh] overflow-y-auto custom-scrollbar" data-id="element-2133">
                  {rooms.map((room, idx) => {
              const Icon = room.icon;
              const isActive = activeRoomIndex === idx;
              return <button key={room.id} onClick={() => handleSelectRoom(idx)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${isActive ? 'bg-white/10' : 'active:bg-white/5'}`} data-id="element-2134">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isActive ? 'bg-white text-black' : 'bg-white/10 text-white/60'}`} data-id="element-2135">
                          <Icon className="w-4 h-4" data-id="element-2136" />
                        </div>
                        <div className="flex-1 text-left" data-id="element-2137">
                          <span className={`text-sm font-medium block ${isActive ? 'text-white' : 'text-white/80'}`} data-id="element-2138">
                            {room.name}
                          </span>
                          <span className="text-xs text-white/40" data-id="element-2139">
                            {room.floor}
                          </span>
                        </div>
                      </button>;
            })}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>, document.body)}

      {/* Floating AI Assistant Button */}
      <motion.button whileTap={{
      scale: 0.92
    }} onClick={() => setIsAssistantActive(!isAssistantActive)} animate={{
      bottom: sheetState === 'collapsed' ? 64 : sheetState === 'peek' ? 230 : 20
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className={`absolute right-4 z-30 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl ${isAssistantActive ? 'bg-blue-500 text-white' : 'bg-white text-black'}`} data-id="element-2140">
        {isAssistantActive ? <Mic className="w-6 h-6 animate-pulse" data-id="element-2141" /> : <Sparkles className="w-6 h-6" data-id="element-2142" />}
        {isAssistantActive && <span className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-75" data-id="element-2143" />}
      </motion.button>

      {/* Bottom Sheet */}
      <MobileOptionsSheet activeItem={activeItem} sheetState={sheetState} onSheetStateChange={setSheetState} onActiveItemChange={handleActiveItemChange} isStepMode currentStepIndex={currentStepIndex} totalSteps={flatSteps.length} currentStepCategory={currentStep?.category} currentStepItemName={currentStep?.item.name} onNextStep={handleNextStep} onPrevStep={handlePrevStep} data-id="element-2144" />
    </div>;
}